import { useRef, useState } from "react"
import { Box, Button, Menu, Tooltip } from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import Iconify from "minimals-template/components/Iconify"
import { Bound } from "lib/@components/binding/Bound"
import { Else, If } from "lib/@components/switch"
import { interactiveStateStyles } from "lib/@components/interactive-state-styles"
import { navFontSx, navTooltipSlotProps } from "routes/@layouts/app/navbar/redesign/nav-font"

export function NavSwitcher({
    id,
    dataCy,
    tooltip,
    hiddenLabel,
    accessibleLabel,
    menuLabel,
    canSwitch,
    isCollapse,
    face,
    children,
}) {
    const anchorRef = useRef(null)
    const [open, setOpen] = useState(false)

    const surfaceSx = [
        {
            width: 1,
            py: 1,
            minWidth: 0,
            minHeight: 72,
            justifyContent: "center",
            color: "text.primary",
            borderRadius: (t) => `${Number(t.shape.borderRadius) * 2}px`,
        },
        !isCollapse && { pl: 1, pr: 3 },
        isCollapse && { px: 0 },
    ]

    const faceContent = (
        <Box
            sx={{
                width: 1,
                display: "inline-flex",
                alignItems: "center",
                gap: 2,
                lineHeight: (t) => t.typography.body1.lineHeight,
                "& svg": { verticalAlign: "middle" },
                ...(isCollapse && { position: "relative", justifyContent: "center" }),
                ...(!isCollapse && { ml: 1 }),
            }}
        >
            <If value={canSwitch && !accessibleLabel}>
                <Box component="span" sx={visuallyHidden}>
                    {hiddenLabel}
                </Box>
            </If>
            {face}
            <If value={canSwitch}>
                <Box
                    sx={{
                        fontSize: "1rem",
                        display: "inline-flex",
                        alignItems: "center",
                        color: "text.secondary",
                        ...(isCollapse && {
                            position: "absolute",
                            right: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                        }),
                        ...(!isCollapse && { ml: "auto" }),
                    }}
                >
                    <Iconify icon="mdi:unfold-more-horizontal" />
                </Box>
            </If>
        </Box>
    )

    return (
        <Box
            ref={anchorRef}
            sx={{
                mb: 1,
                mx: 2,
            }}
        >
            <If value={canSwitch}>
                <Tooltip title={tooltip} arrow placement="right" describeChild slotProps={navTooltipSlotProps}>
                    <Button
                        onClick={toggle}
                        variant="text"
                        disableFocusRipple
                        aria-label={accessibleLabel}
                        aria-haspopup="menu"
                        aria-expanded={open}
                        aria-controls={open ? id : undefined}
                        data-cy={dataCy}
                        onKeyDown={handleTriggerKeyDown}
                        sx={[interactiveStateStyles, ...surfaceSx]}
                    >
                        {faceContent}
                    </Button>
                </Tooltip>

                <Else>
                    <Tooltip title={tooltip} arrow placement="right" describeChild slotProps={navTooltipSlotProps}>
                        <Box component="dl" data-cy={dataCy} sx={{ m: 0 }}>
                            <Box component="dt" sx={visuallyHidden}>
                                {hiddenLabel}
                            </Box>
                            <Box component="dd" sx={[{ m: 0, display: "flex", alignItems: "center" }, ...surfaceSx]}>
                                {faceContent}
                            </Box>
                        </Box>
                    </Tooltip>
                </Else>
            </If>

            <If value={canSwitch}>
                <Menu
                    id={id}
                    open={open}
                    anchorEl={anchorRef.current}
                    onClose={close}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    marginThreshold={0}
                    slotProps={{
                        paper: {
                            sx: {
                                ...navFontSx,
                                borderRadius: 1.5,
                                width: isCollapse ? undefined : anchorRef.current?.offsetWidth,
                                minWidth: 220,
                                p: 0.5,
                            },
                        },
                    }}
                    MenuListProps={{ "aria-label": menuLabel, dense: true }}
                >
                    <Bound closeSwitcher={close}>{children}</Bound>
                </Menu>
            </If>
        </Box>
    )

    function toggle() {
        setOpen((v) => !v)
    }

    function handleTriggerKeyDown(event) {
        if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return
        event.preventDefault()
        setOpen(true)
    }

    function close() {
        setOpen(false)
    }
}
